import { middlewareGateway } from '~/gateway/middleware.gateway'

export default function useResetPassword() {
  const error = ref<null | Error>(null)
  const { hasFrontendFeatureSwitch } = useFeatureFlags()

  async function sendResetPasswordToken(email: string) {
    if (!email) {
      error.value = new Error('No email provided')
      return false
    }

    const nuxtApp = useNuxtApp()
    const locale =
      nuxtApp.$i18n.locale.value === 'en-CA' || nuxtApp.$i18n.locale.value === 'fr'
        ? localeConfig[nuxtApp.$i18n.locale.value].code
        : ''
    // SFCC expects the parameter omitted for other locales; an empty string is not a valid locale.
    const localeParams: { locale?: string } = locale ? { locale } : {}

    if (hasFrontendFeatureSwitch('braze-emails')) {
      const { getRecaptchaToken } = useRecaptcha()
      const recaptchaToken = (await getRecaptchaToken(undefined, 'password_reset').catch(() => null)) as string | null
      if (!recaptchaToken) {
        error.value = new Error('Failed to obtain reCAPTCHA token')
        return false
      }

      const response = await middlewareGateway('/next-gen/mhj/braze/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({
          email,
          recaptchaToken,
          ...localeParams,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      }).catch((e) => new Error(e))

      if (response instanceof Error) {
        error.value = response
        return false
      }

      return true
    }

    const shopper = useShopper()
    const resetToken = await shopper.shopperLogin
      .getPasswordResetToken({
        body: {
          user_id: email,
          mode: 'callback',
          callback_uri: getResetPasswordTokenUrl(locale),
          channel_id: shopper.config.siteId,
          client_id: shopper.config.clientId,
          hint: 'cross_device',
          ...localeParams,
        },
      })
      .catch((e) => new Error(e))

    if (resetToken instanceof Error) {
      error.value = resetToken
      return false
    }

    return true
  }

  async function resetPassword({ token, email, newPassword }: { token: string; email: string; newPassword: string }) {
    const shopper = useShopper()
    if (!token || !newPassword) {
      error.value = new Error('Reset link expired, please try again')
      return false
    }
    const resetPassword = await shopper.shopperLogin
      .resetPassword({
        // @ts-ignore, this is a bug in the types from commerce-sdk-isomorphic package, docs state this is valid and have verified this payload works as expected
        // reported issue to sfcc, https://github.com/SalesforceCommerceCloud/commerce-sdk-isomorphic/issues/261
        body: {
          user_id: email,
          new_password: newPassword,
          pwd_action_token: token,
          channel_id: shopper.config.siteId,
          client_id: shopper.config.clientId,
          hint: 'cross_device',
        },
      })
      .catch((e) => new Error(e))

    if (resetPassword instanceof Error) {
      error.value = resetPassword
      return false
    }
    return true
  }
  return { sendResetPasswordToken, error, resetPassword }
}
